// Data for the tense pages (/grammar/kal/[tense]): the URL slug of every tense, what each tense is for, the hub's
// groups, the roots each page offers and the subject pronouns of every person and number (from the engine's pronoun
// tables). Every Prakrit form on these pages comes from the grammar engine (verbs.ts, pratyaya.ts).

import type {
    Lang,
    Num,
    Person,
    Tense
} from '@/prakrit/lib/grammar/types'
import type { LabRoot } from '@/prakrit/components/grammar/labs/verb-data'

import { PRONOUNS, PRONOUN_META } from '@/prakrit/lib/grammar/pronouns'
import { VIKALP_SUBJECTS, hasPratyaya } from '@/prakrit/lib/grammar/pratyaya'
import { TENSES } from '@/prakrit/lib/grammar/types'
import { labRoots } from '@/prakrit/components/grammar/labs/verb-data'

/** The root every tense page opens with (the reference uses हस, हँसना). */
export const DEFAULT_KAL_ROOT = 'हस'

/** A long (आ-ending) root to show the -ही / -सी / -हीअ endings of the past and the दे- variants. */
export const LONG_EXAMPLE_ROOT = 'दा'

export const tenseSlug = (tense: Tense): string => tense.replace(/_/g, '-')

export const tenseOfSlug = (slug: string): Tense | null => {
    const tense = slug.replace(/-/g, '_')

    return (TENSES as readonly string[]).includes(tense) ? tense as Tense : null
}

export const kalHref = (tense: Tense, root?: string): string => (
    `/grammar/kal/${tenseSlug(tense)}${root && root !== DEFAULT_KAL_ROOT ? `?root=${encodeURIComponent(root)}` : ''}`
)

export interface TenseInfo {
    /** What the tense is for, in a sentence. */
    use: Lang
    /** A shorter line for Kids mode. */
    kids: Lang
}

export const TENSE_INFO: Record<Tense, TenseInfo> = {
    vartaman: {
        use: { hi: 'जो काम अभी हो रहा है या रोज़ होता है। पुरुष और वचन बदलने पर प्रत्यय बदलता है।', en: 'What is happening now or happens every day. The ending changes with person and number.' },
        kids: { hi: 'अभी या रोज़ होने वाला काम: मैं हँसता हूँ।', en: 'Things happening now: I laugh.' }
    },
    bhoot: {
        use: { hi: 'जो काम हो चुका। अकारान्त धातु में सभी पुरुषों और दोनों वचनों का एक ही रूप होता है।', en: 'What already happened. A root ending in अ has one form for every person and both numbers.' },
        kids: { hi: 'जो हो चुका: मैं हँसा। सबके लिए एक ही रूप!', en: 'Things that already happened: I laughed. One form for everyone!' }
    },
    bhavishya: {
        use: { hi: 'जो काम आगे होगा। पहले धातु का अ → इ होता है, फिर प्रत्यय जुड़ता है।', en: 'What will happen. First the अ of the root becomes इ, then the ending is added.' },
        kids: { hi: 'जो आगे होगा: मैं हँसूँगा।', en: 'Things that will happen: I will laugh.' }
    },
    vidhi: {
        use: { hi: 'आज्ञा, प्रार्थना, उपदेश और "चाहिए" के लिए। मना करने के लिए: मा + रूप।', en: 'For commands, requests, advice and "should". To forbid: मा + the form.' },
        kids: { hi: 'कहना कि कोई काम करे: तुम हँसो!', en: 'Telling someone to do something: laugh!' }
    },
    karmani_vartaman: {
        use: { hi: 'कर्मवाच्य: "किया जाता है"। कर्ता तृतीया में, कर्म प्रथमा में; क्रिया कर्म के पुरुष-वचन के अनुसार।', en: 'Passive: "is done". The doer goes into तृतीया, the thing done into प्रथमा, and the verb follows it.' },
        kids: { hi: 'जिस पर काम होता है, वही कर्ता की जगह आता है।', en: 'The one something happens to comes first.' }
    },
    karmani_bhoot: {
        use: { hi: 'कर्मवाच्य भूत: "किया गया"। सभी पुरुष-वचन में एक-से रूप।', en: 'Passive past: "was done". The same forms for every person and number.' },
        kids: { hi: 'जिस पर काम हो चुका।', en: 'Something that was done to someone.' }
    },
    karmani_vidhi: {
        use: { hi: 'कर्मवाच्य आज्ञा: "किया जाए"।', en: 'Passive command: "let it be done".' },
        kids: { hi: 'कहना कि किसी पर काम किया जाए।', en: 'Asking that something be done.' }
    },
    prerak_vartaman: {
        use: { hi: 'प्रेरणार्थक: किसी से काम करवाना, "करवाता है"। धातु + आव, फिर प्रत्यय।', en: 'Causative: making someone do it, "makes do". Root + आव, then the ending.' },
        kids: { hi: 'किसी से काम करवाना: मैं हँसाता हूँ।', en: 'Making someone do it: I make (them) laugh.' }
    },
    prerak_bhoot: {
        use: { hi: 'प्रेरणार्थक भूत: "करवाया"। सभी पुरुष-वचन में एक रूप।', en: 'Causative past: "made do". One form for every person and number.' },
        kids: { hi: 'किसी से काम करवाया।', en: 'Made someone do it.' }
    },
    prerak_bhavishya: {
        use: { hi: 'प्रेरणार्थक भविष्यत्: "करवाएगा"। आव का अ → इ, फिर भविष्यत् का प्रत्यय।', en: 'Causative future: "will make do". The अ of आव becomes इ, then the future ending.' },
        kids: { hi: 'आगे किसी से काम करवाएगा।', en: 'Will make someone do it.' }
    },
    prerak_vidhi: {
        use: { hi: 'प्रेरणार्थक आज्ञा: "करवाओ"।', en: 'Causative command: "make (them) do it".' },
        kids: { hi: 'कहना कि किसी से काम करवाओ।', en: 'Telling someone to make another do it.' }
    },
    kriyatipatti: {
        use: { hi: '"यदि … तो …" (जइ … ता …): जो हो सकता था पर नहीं हुआ। सभी पुरुष-वचन में एक-से रूप।', en: '"If … then …" (जइ … ता …): what could have happened but did not. The same forms for every person and number.' },
        kids: { hi: 'अगर ऐसा होता, तो वैसा होता।', en: 'If this had happened, that would have happened.' }
    }
}

export interface TenseGroup {
    id: string
    title: Lang
    note: Lang
    tenses: Tense[]
    /** Kids see the first group open; the rest are for Adults first (still open to everyone). */
    basic: boolean
}

export const TENSE_GROUPS: TenseGroup[] = [
    {
        id: 'mool',
        title: { hi: 'चार मुख्य काल', en: 'The four main tenses' },
        note: { hi: 'पहले इन्हें सीखें, इसी क्रम में।', en: 'Learn these first, in this order.' },
        tenses: ['vartaman', 'vidhi', 'bhoot', 'bhavishya'],
        basic: true
    },
    {
        id: 'karmani',
        title: { hi: 'कर्मवाच्य', en: 'Passive' },
        note: { hi: 'धातु + ईअ / इज्ज, फिर काल का प्रत्यय।', en: 'Root + ईअ / इज्ज, then the tense ending.' },
        tenses: ['karmani_vartaman', 'karmani_bhoot', 'karmani_vidhi'],
        basic: false
    },
    {
        id: 'prerak',
        title: { hi: 'प्रेरणार्थक', en: 'Causative' },
        note: { hi: 'धातु + आव (विकल्प: आवे), फिर प्रत्यय।', en: 'Root + आव (variant आवे), then the ending.' },
        tenses: ['prerak_vartaman', 'prerak_bhoot', 'prerak_bhavishya', 'prerak_vidhi'],
        basic: false
    },
    {
        id: 'kriyatipatti',
        title: { hi: 'क्रियातिपत्ति', en: 'Conditional' },
        note: { hi: 'जइ … ता … वाले वाक्यों के लिए।', en: 'For "if … then …" sentences.' },
        tenses: ['kriyatipatti'],
        basic: false
    }
]

/** Every tense in the order of the hub. */
export const KAL_ORDER: Tense[] = TENSE_GROUPS.flatMap(g => g.tenses)

/** Kit roots (no अस) that conjugate in this tense, in kit order. */
export const rootsForTense = (tense: Tense): LabRoot[] => labRoots().filter(r => r.group !== 'as' && hasPratyaya(tense, r.rootClass) && Boolean(r.tables[tense]))

// ---------------------------------------------------------------- subjects (प्रथमा, from the pronoun tables)

export interface CellSubjects {
    /** The core प्रथमा pronouns of this person and number (अहं; सो, सा, तं, इमो …). */
    book: string[]
    /** The pronouns the विकल्प view pairs with every form, as the notes give them (अहं, हं, अम्मि …). */
    vikalp: string[]
}

const uniq = (list: string[]): string[] => Array.from(new Set(list))

let subjectCache: Record<Person, Record<Num, CellSubjects>> | null = null

export const subjectsOf = (p: Person, n: Num): CellSubjects => {
    if (!subjectCache) {
        const out = {} as Record<Person, Record<Num, CellSubjects>>

        for (const person of ['uttam', 'madhyam', 'anya'] as Person[]) {
            out[person] = {} as Record<Num, CellSubjects>

            for (const num of ['eka', 'bahu'] as Num[]) {
                const metas = PRONOUN_META.filter(m => m.person === person)
                const cells = metas.map(m => PRONOUNS[m.key]?.prathama?.[num] ?? [])

                out[person][num] = {
                    book: uniq(cells.flatMap(c => c.filter(f => f.status === 'core').map(f => f.form))),
                    vikalp: VIKALP_SUBJECTS[person][num]
                }
            }
        }

        subjectCache = out
    }

    return subjectCache[p][n]
}

/** Words of the विकल्प sentences that are not tappable: ता (= ताओ) is also the अव्यय "तो", so the Word Explorer would mislead. */
export const PLAIN_SUBJECTS = new Set(['ता'])

// ---------------------------------------------------------------- the rules of each tense (from the teacher's notes)

export interface TenseNotes {
    /** The notes' rules, one line each. */
    rules: Lang[]
    /** Asides about Ardhamāgadhī and rare forms (Adults only); never asked. */
    asides?: Lang[]
}

export const TENSE_NOTES: Partial<Record<Tense, TenseNotes>> = {
    vartaman: {
        rules: [
            { hi: 'उत्तम पुरुष एकवचन में मि; मि लगने पर धातु के अन्त्य अ का आ और ए भी होता है: हसमि, हसामि, हसेमि।', en: 'Speaker, singular: -मि; before it the final अ of the root may also become आ or ए: हसमि, हसामि, हसेमि.' },
            { hi: 'मध्यम पुरुष एकवचन में सि और से; सि से पहले अ → ए भी। से केवल अकारान्त धातु के साथ।', en: 'Addressee, singular: -सि and -से; before -सि the अ may become ए. -से goes only with roots ending in अ.' },
            { hi: 'अन्य पुरुष एकवचन में इ, ए, दि, दे; इ और दि से पहले अ → ए भी। ए और दे केवल अकारान्त धातु के साथ।', en: 'The other, singular: -इ, -ए, -दि, -दे; before -इ and -दि the अ may become ए. -ए and -दे go only with roots ending in अ.' },
            { hi: 'उत्तम पुरुष बहुवचन में मो, मु, म; इनसे पहले अ का आ, इ और ए भी होता है (१२ रूप)।', en: 'Speakers, plural: -मो, -मु, -म; before them the अ may become आ, इ or ए (12 forms).' },
            { hi: 'मध्यम पुरुष बहुवचन में ह, इत्था, ध; इनसे पहले अ → ए भी।', en: 'Addressees, plural: -ह, -इत्था, -ध; before them the अ may become ए.' },
            { hi: 'अन्य पुरुष बहुवचन में न्ति, न्ते, इरे; न्ति से पहले अ → ए भी।', en: 'The others, plural: -न्ति, -न्ते, -इरे; before -न्ति the अ may become ए.' },
            { hi: 'तीनों पुरुषों के दोनों वचनों में ज्ज, ज्जा भी लगते हैं (अ → ए): हसेज्ज, हसेज्जा; ठाज्ज, ठाज्जा और अ विकरण के साथ ठाएज्ज, ठाएज्जा।', en: 'Every person and number also takes -ज्ज, -ज्जा (अ becomes ए): हसेज्ज, हसेज्जा; ठाज्ज, ठाज्जा, and with the vikaraṇa अ: ठाएज्ज, ठाएज्जा.' },
            { hi: 'आकारान्त आदि धातु (ठा, ण्हा, हो): अन्त्य स्वर नहीं बदलता; से, ए, दे नहीं लगते; संयुक्त अक्षर से पहले दीर्घ स्वर ह्रस्व होता है: ठान्ति → ठन्ति।', en: 'Roots ending in आ, ओ … (ठा, ण्हा, हो): the final vowel does not change; -से, -ए, -दे are not used; a long vowel before a conjunct becomes short: ठान्ति → ठन्ति.' }
        ],
        asides: [
            { hi: 'कभी-कभी हसमि आदि के स्थान पर हसं, णच्चं जैसे रूप भी मिलते हैं (हे. प्रा. व्या. ३-१४१)।', en: 'Sometimes forms like हसं, णच्चं are found instead of हसमि (Hemacandra 3.141).' },
            { hi: 'अन्य पुरुष एकवचन में ति भी मिलता है (हसति, हसेति); पं. बेचरदास जी ने ते भी माना है (हसते)।', en: 'The other, singular also shows -ति (हसति, हसेति); Pt. Becardas also accepts -ते (हसते).' }
        ]
    },
    vidhi: {
        rules: [
            { hi: 'प्रार्थना, आज्ञा और उपदेश के भाव में विधि एवं आज्ञा के प्रत्यय लगते हैं।', en: 'The endings of विधि एवं आज्ञा express a request, a command or advice.' },
            { hi: 'उत्तम पुरुष एकवचन में मु; अ का आ, इ, ए भी: हसमु, हसामु, हसिमु, हसेमु।', en: 'Speaker, singular: -मु; the अ may become आ, इ or ए: हसमु, हसामु, हसिमु, हसेमु.' },
            { hi: 'मध्यम पुरुष एकवचन में हि, सु, धि (अ → ए भी), शून्य प्रत्यय (०) और इज्जसु, इज्जहि, इज्जे (अ + इ = ए): कुल १० रूप।', en: 'Addressee, singular: -हि, -सु, -धि (अ may become ए), the zero ending (०) and -इज्जसु, -इज्जहि, -इज्जे (अ + इ = ए): 10 forms in all.' },
            { hi: 'अन्य पुरुष एकवचन में उ, दु; अ → ए भी।', en: 'The other, singular: -उ, -दु; the अ may become ए.' },
            { hi: 'उत्तम पुरुष बहुवचन में मो; अ का आ और ए भी।', en: 'Speakers, plural: -मो; the अ may become आ or ए.' },
            { hi: 'मध्यम पुरुष बहुवचन में ह, ध; अन्य पुरुष बहुवचन में न्तु; दोनों में अ → ए भी।', en: 'Addressees, plural: -ह, -ध; the others, plural: -न्तु; in both the अ may become ए.' },
            { hi: 'आकारान्त आदि धातु: मध्यम पुरुष एकवचन में केवल हि, सु, धि (शून्य और इज्जसु आदि नहीं); ठान्तु → ठन्तु।', en: 'Roots ending in आ, ओ …: in the addressee singular only -हि, -सु, -धि (not the zero ending or -इज्जसु …); ठान्तु → ठन्तु.' }
        ],
        asides: [
            { hi: 'अर्द्धमागधी में विधि के प्रत्यय: एज्जा, एज्जामि · एज्जा, एज्जासि, एज्जाहि · ए, एज्जा · एज्जाम · एज्जाह · एज्जा।', en: 'In Ardhamāgadhī the endings of विधि are: एज्जा, एज्जामि · एज्जा, एज्जासि, एज्जाहि · ए, एज्जा · एज्जाम · एज्जाह · एज्जा.' }
        ]
    },
    bhoot: {
        rules: [
            { hi: 'अकारान्त धातु में सभी पुरुषों और दोनों वचनों में एक ही प्रत्यय ईअ लगता है: हसीअ।', en: 'A root ending in अ takes the single ending -ईअ for every person and both numbers: हसीअ.' },
            { hi: 'आकारान्त आदि धातु में सी, ही, हीअ तीनों लगते हैं: ठासी, ठाही, ठाहीअ; होसी, होही, होहीअ।', en: 'Roots ending in आ, ओ … take -सी, -ही and -हीअ: ठासी, ठाही, ठाहीअ; होसी, होही, होहीअ.' }
        ],
        asides: [
            { hi: 'अर्द्धमागधी में सभी पुरुष-वचनों में इत्था और इंसु: हसित्था, हसिंसु; ठाइत्था, ठाइंसु। होत्था (हुआ), आहंसु (कहा) भी मिलते हैं।', en: 'In Ardhamāgadhī every person and number takes -इत्था and -इंसु: हसित्था, हसिंसु; ठाइत्था, ठाइंसु. होत्था (was) and आहंसु (said) are also found.' }
        ]
    },
    bhavishya: {
        rules: [
            { hi: 'पहले भविष्यत् काल का प्रत्यय, फिर वर्तमान काल का प्रत्यय: हस + (इ/ए) + हि + मि = हसिहिमि।', en: 'First the future marker, then the present ending: हस + (इ/ए) + हि + मि = हसिहिमि.' },
            { hi: 'उत्तम पुरुष एकवचन: हि, स्सा, हा (+ मि, अ → इ/ए), स्सि (+ मि, केवल इ), स्सं (पूर्ण प्रत्यय, मि नहीं): ९ रूप।', en: 'Speaker, singular: -हि, -स्सा, -हा (+ मि, अ becomes इ or ए), -स्सि (+ मि, only इ), -स्सं (complete, no मि): 9 forms.' },
            { hi: 'मध्यम पुरुष एकवचन: हि, स्स (+ सि / से, अ → इ/ए), स्सि (+ सि / से, केवल इ): १० रूप।', en: 'Addressee, singular: -हि, -स्स (+ सि / से, अ becomes इ or ए), -स्सि (+ सि / से, only इ): 10 forms.' },
            { hi: 'बाकी चार खाने नोट्स में अभी नहीं हैं; उनके विकल्प इसी क्रम से बनाए गए हैं (भविष्यत् प्रत्यय + उस खाने का वर्तमान प्रत्यय) और शिक्षक की पुष्टि बाकी है।', en: 'The other four cells are not in the notes yet; their variants are built by the same formula (future marker + that cell’s present ending) and still need a teacher’s confirmation.' }
        ],
        asides: [
            { hi: 'सोच्छ, गच्छ, रोच्छ आदि में हि का लोप होकर सीधे वर्तमान प्रत्यय: सोच्छं, गच्छं; सोच्छिमि, सोच्छेमि। काहं (करूँगा), दाहं (दूँगा) भी।', en: 'In सोच्छ, गच्छ, रोच्छ … the future -हि drops and the present ending is added directly: सोच्छं, गच्छं; सोच्छिमि, सोच्छेमि. Also काहं (I will do), दाहं (I will give).' }
        ]
    }
}
